/** @format */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
	User,
	KeyRound,
	Mail,
	Copy,
	Gift,
	AlertCircle,
	Loader2,
	Check,
	Eye,
	EyeOff,
} from "lucide-react"; // Added Eye/EyeOff

// Actions & Components (Ensure paths are correct)
import { getUserDetails, updateUserPassword } from "../actions/userActions"; // Removed unused logout, clearError? Add if needed.
import { FormInput } from "../components/FormInput";
import { LoadingSpinner } from "../components/LoadingSpinner"; // Assuming path
import { Toast } from "./customize/Toast"; // Assuming path from previous context
import { Helmet } from "react-helmet";
import { Link } from "lucide-react";

// --- Main Profile Screen ---
const ProfileScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// --- State ---
	const [passwordData, setPasswordData] = useState({
		password: "",
		confirmPassword: "",
	});
	const [passwordErrors, setPasswordErrors] = useState({
		password: "",
		confirmPassword: "",
	});
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [referralCode, setReferralCode] = useState("");
	const [message, setMessage] = useState(""); // General success/info message (e.g., code copied)
	const [copied, setCopied] = useState(false);
	const [updateStatus, setUpdateStatus] = useState({
		loading: false,
		error: null,
		success: false,
	}); // State for password update action

	// --- Redux State ---
	const {
		loading: detailsLoading,
		error: detailsError,
		user,
	} = useSelector((state) => state.userDetails);
	const { userInfo } = useSelector((state) => state.userLogin); // Need this for checks and potentially dispatching updates

	// --- Effects ---
	// Fetch user details or redirect if not logged in
	useEffect(() => {
		if (!userInfo) {
			navigate("/login?redirect=/profile"); // Redirect to login if not authenticated
		} else if (!user?.id) {
			// Fetch details only if not already loaded
			// Pass userInfo to ensure action uses the correct user context
			dispatch(getUserDetails(userInfo));
		}
	}, [navigate, dispatch, userInfo, user?.id]); // Added user.id dependency

	// Generate referral code once user data is available
	useEffect(() => {
		if (user?.id && user?.name) {
			// Simple concatenation: FirstName + ID (uppercase)
			setReferralCode(
				`${user.name.split(" ")[0] || "USER"}${user.id}`.toUpperCase()
			);
		}
	}, [user?.id, user?.name]);

	// Clear messages after a delay
	useEffect(() => {
		let timer;
		if (message || updateStatus.error || updateStatus.success) {
			timer = setTimeout(() => {
				setMessage("");
				setUpdateStatus({ loading: false, error: null, success: false }); // Reset update status too
			}, 4000); // Clear after 4 seconds
		}
		return () => clearTimeout(timer); // Cleanup timer on unmount or if message changes
	}, [message, updateStatus.error, updateStatus.success]);

	// --- Validation ---
	const validatePassword = useCallback((password) => {
		if (!password) return "";
		if (password.length < 6) return "Password must be at least 6 characters.";
		if (!/[0-9]/.test(password))
			return "Password must contain at least one number.";
		return "";
	}, []);

	// --- Handlers ---
	const handleInputChange = useCallback(
		(e) => {
			const { name, value } = e.target;
			setPasswordData((prev) => ({ ...prev, [name]: value }));

			// --- Client-side Password Validation ---
			let pwdError = passwordErrors.password;
			let confirmPwdError = passwordErrors.confirmPassword;

			if (name === "password") {
				pwdError = validatePassword(value);
				// Re-check confirm password if password changes
				confirmPwdError =
					passwordData.confirmPassword && value !== passwordData.confirmPassword
						? "Passwords do not match."
						: "";
			}
			if (name === "confirmPassword") {
				confirmPwdError =
					value && value !== passwordData.password
						? "Passwords do not match."
						: "";
			}
			setPasswordErrors({
				password: pwdError,
				confirmPassword: confirmPwdError,
			});
		},
		[
			passwordData.password,
			passwordData.confirmPassword,
			validatePassword,
			passwordErrors.password,
			passwordErrors.confirmPassword,
		]
	); // Dependencies

	const handleCopyRefCode = useCallback(() => {
		if (!referralCode) return;
		navigator.clipboard
			.writeText(referralCode)
			.then(() => {
				setCopied(true);
				setMessage("Referral code copied!"); // Use general message state
				setTimeout(() => setCopied(false), 2000); // Reset copied visual state
			})
			.catch((err) => {
				console.error("Failed to copy referral code:", err);
				setMessage("Could not copy code. Please try manually."); // Use general message state
			});
	}, [referralCode]);

	const handlePasswordUpdate = async (e) => {
		e.preventDefault();
		setMessage(""); // Clear previous messages
		setUpdateStatus({ loading: true, error: null, success: false }); // Set loading

		// Final validation check
		const finalPwdError = validatePassword(passwordData.password);
		const finalConfirmError =
			passwordData.password !== passwordData.confirmPassword
				? "Passwords do not match."
				: "";
		setPasswordErrors({
			password: finalPwdError,
			confirmPassword: finalConfirmError,
		});

		if (finalPwdError || finalConfirmError) {
			setUpdateStatus({
				loading: false,
				error: "Please fix the errors above.",
				success: false,
			});
			return; // Stop if client errors
		}

		try {
			// Dispatch action and wait for result (assuming action handles async internally and updates Redux state)
			// Pass necessary identifiers (like userInfo.id or token) and the new password
			// The action should handle success/error states in Redux (e.g., USER_UPDATE_PASSWORD_SUCCESS/FAIL)
			await dispatch(updateUserPassword(userInfo, passwordData.password)); // Adjust args as needed by your action

			// If action throws error, it will be caught below. If successful:
			setUpdateStatus({ loading: false, error: null, success: true });
			setMessage("Password updated successfully!"); // Use general message state
			setPasswordData({ password: "", confirmPassword: "" }); // Clear fields
			setPasswordErrors({ password: "", confirmPassword: "" }); // Clear errors
		} catch (error) {
			console.error("Password update failed:", error);
			// Extract a user-friendly error message if possible
			const errMsg =
				error?.response?.data?.message ||
				error.message ||
				"Password update failed. Please try again.";
			setUpdateStatus({ loading: false, error: errMsg, success: false });
		}
	};

	// --- Derived State ---
	// Check if password fields are valid for enabling button
	const canUpdatePassword =
		passwordData.password &&
		passwordData.confirmPassword &&
		!passwordErrors.password &&
		!passwordErrors.confirmPassword;

	// --- Initial Loading State ---
	if (detailsLoading && !user?.id) {
		// Show full page spinner only on initial load
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<LoadingSpinner text="Loading your profile..." variant="full-page" />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-100 py-10 md:py-12">
			{" "}
			{/* Changed background */}
			<Helmet>
				{" "}
				<title>My Profile | Off Cut Shades</title>{" "}
				<meta name="robots" content="noindex,nofollow" />{" "}
			</Helmet>
			<div className="container mx-auto px-4 max-w-3xl">
				{" "}
				{/* Constrained width */}
				{/* Header */}
				<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8 pb-4 border-b border-gray-200">
					<div className="flex items-center gap-3">
						<User className="w-8 h-8 text-primary" />
						<h1 className="text-2xl md:text-3xl font-bold text-gray-900">
							My Profile
						</h1>
					</div>
					{/* Link to Orders */}
					<Link
						to="/myorders"
						className="text-sm text-primary hover:underline font-medium"
					>
						View My Orders
					</Link>
				</div>
				{/* Main Content Grid */}
				<div className="space-y-6 md:space-y-8">
					{/* --- Account Information Card --- */}
					<motion.section
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.1 }}
					>
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
							<h2 className="text-lg font-semibold mb-5 text-gray-800">
								Account Information
							</h2>
							{/* Display details loading error if occurred */}
							{detailsError && !user?.id && (
								<Toast
									show
									type="error"
									message={`Error loading profile: ${detailsError}`}
								/>
							)}

							{/* Display read-only user info */}
							<div className="space-y-4 max-w-md">
								<FormInput
									label="Name"
									type="text"
									value={user?.name || "Loading..."}
									icon={<User />}
									disabled
									readOnly
								/>
								<FormInput
									label="Email"
									type="email"
									value={user?.email || "Loading..."}
									icon={<Mail />}
									disabled
									readOnly
								/>
							</div>
						</div>
					</motion.section>

					{/* --- Change Password Card --- */}
					<motion.section
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.2 }}
					>
						<form onSubmit={handlePasswordUpdate}>
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
								<h2 className="text-lg font-semibold mb-5 text-gray-800">
									Change Password
								</h2>
								{/* Display password update status */}
								<AnimatePresence>
									{updateStatus.error && (
										<Toast show type="error" message={updateStatus.error} />
									)}
									{updateStatus.success && (
										<Toast
											show
											type="success"
											message="Password updated successfully!"
										/>
									)}
								</AnimatePresence>

								<div className="space-y-4 max-w-md mt-4">
									<div>
										<FormInput
											label="New Password"
											name="password"
											type={showPassword ? "text" : "password"}
											id="new-password"
											icon={<KeyRound />}
											value={passwordData.password}
											onChange={handleInputChange}
											error={passwordErrors.password}
											placeholder="Enter new password"
											autoComplete="new-password"
											disabled={updateStatus.loading}
											required
											// Addon for visibility toggle
											addonRight={
												<button
													type="button"
													onClick={() => setShowPassword(!showPassword)}
													className="p-1 text-gray-500 hover:text-gray-700"
													aria-label={
														showPassword ? "Hide password" : "Show password"
													}
												>
													{showPassword ? (
														<EyeOff size={18} />
													) : (
														<Eye size={18} />
													)}
												</button>
											}
										/>
										{/* Show requirements only if password has content and has errors, or simply always if focused */}
										{/* {(passwordData.password || passwordErrors.password) && (
											<PasswordRequirements password={passwordData.password} />
										)} */}
									</div>
									<FormInput
										label="Confirm New Password"
										name="confirmPassword"
										type={showConfirmPassword ? "text" : "password"}
										id="confirm-password"
										icon={<KeyRound />}
										value={passwordData.confirmPassword}
										onChange={handleInputChange}
										error={passwordErrors.confirmPassword}
										placeholder="Confirm new password"
										autoComplete="new-password"
										disabled={updateStatus.loading}
										required
										addonRight={
											<button
												type="button"
												onClick={() =>
													setShowConfirmPassword(!showConfirmPassword)
												}
												className="p-1 text-gray-500 hover:text-gray-700"
												aria-label={
													showConfirmPassword
														? "Hide password"
														: "Show password"
												}
											>
												{showConfirmPassword ? (
													<EyeOff size={18} />
												) : (
													<Eye size={18} />
												)}
											</button>
										}
									/>
								</div>
								<div className="mt-6">
									<button
										type="submit"
										disabled={updateStatus.loading || !canUpdatePassword} // Disable if loading or invalid
										className="btn btn-primary px-6 py-2.5 text-sm" // Smaller button
									>
										{updateStatus.loading ? (
											<LoadingSpinner variant="button" size="sm" />
										) : (
											"Update Password"
										)}
									</button>
								</div>
							</div>
						</form>
					</motion.section>

					{/* --- Referral Program Card --- */}
					<motion.section
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.3 }}
					>
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
							<div className="flex flex-col sm:flex-row items-start gap-4">
								<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
									<Gift className="w-6 h-6 text-primary" />
								</div>
								<div className="flex-1">
									<h2 className="text-lg font-semibold mb-2 text-gray-800">
										Share & Save: Referral Program
									</h2>
									<p className="text-sm text-gray-600 mb-4">
										Give your friends{" "}
										<strong className="font-medium">10% off</strong> their first
										order, and get{" "}
										<strong className="font-medium">store credit</strong>{" "}
										applied to your account for every successful referral. Share
										your unique code below!
									</p>
									<div className="flex flex-col sm:flex-row items-stretch gap-2">
										<div className="relative flex-grow">
											<input
												type="text"
												value={referralCode || "Loading..."}
												readOnly
												className="w-full pl-3 pr-10 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium text-sm"
												aria-label="Your Referral Code"
											/>
											{/* Position copy button inside */}
											<button
												onClick={handleCopyRefCode}
												disabled={!referralCode}
												className={`absolute right-1 top-1 bottom-1 px-3 rounded-md flex items-center gap-1.5 text-sm transition-colors ${
													copied
														? "bg-green-100 text-green-700"
														: "bg-gray-200 text-gray-600 hover:bg-gray-300"
												}`}
												aria-live="polite" // Announce change when copied
											>
												{copied ? <Check size={16} /> : <Copy size={16} />}
												{copied ? "Copied" : "Copy"}
											</button>
										</div>
									</div>
									{/* Display copy message */}
									<AnimatePresence>
										{message && (
											<motion.p
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												exit={{ opacity: 0 }}
												className="text-xs text-green-600 mt-2"
											>
												{message}
											</motion.p>
										)}
									</AnimatePresence>
								</div>
							</div>
						</div>
					</motion.section>
				</div>
				{/* --- Toast Notifications --- */}
				{/* Use Toast for general errors/success not handled inline */}
			</div>
		</div>
	);
};

export default ProfileScreen;

// Add base styles if needed
/*
@layer components {
  .form-input { @apply w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary/50 focus:border-primary outline-none transition duration-150 ease-in-out text-sm disabled:bg-gray-100 disabled:cursor-not-allowed; }
  .btn { @apply px-5 py-2.5 rounded-lg font-semibold text-center shadow-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed; }
  .btn-primary { @apply bg-primary text-white hover:bg-primary/90; }
}
*/
